import { useLingui } from "@lingui/react/macro";
import { TextQuote } from "lucide-react";
import { memo, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const QuoteSelectionButton = memo(function QuoteSelectionButton({
  range,
  boundary,
  onQuote,
}: {
  range: Range;
  boundary: HTMLElement | null;
  onQuote: () => void;
}) {
  const { t } = useLingui();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      if (range.collapsed || !document.contains(range.commonAncestorContainer)) {
        setPlacement(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      const width = buttonRef.current?.offsetWidth ?? 0;
      const height = buttonRef.current?.offsetHeight ?? 0;
      const bounds = boundary?.getBoundingClientRect();
      const top = Math.max(0, bounds?.top ?? 0) + 8;
      const bottom = Math.min(window.innerHeight, bounds?.bottom ?? window.innerHeight) - 8;
      const left = Math.max(0, bounds?.left ?? 0) + width / 2 + 8;
      const right = Math.min(window.innerWidth, bounds?.right ?? window.innerWidth) - width / 2 - 8;
      const anchor = rect.top - height - 8 >= top ? rect.top - height - 8 : rect.bottom + 8;
      setPlacement({
        top: Math.max(top, Math.min(anchor, bottom - height)),
        left: Math.max(left, Math.min(rect.left + rect.width / 2, right)),
      });
    };
    update();
    window.addEventListener("resize", update);
    // Scroll doesn't bubble — listen on the capture phase to catch any scroller.
    window.addEventListener("scroll", update, { capture: true, passive: true });
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [range, boundary]);

  return createPortal(
    <button
      ref={buttonRef}
      type="button"
      data-quote-selection
      data-testid="quote-selection"
      onMouseDown={(event) => {
        // Keep the highlight alive until the click commits the quote.
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={onQuote}
      style={placement ? { top: placement.top, left: placement.left } : { visibility: "hidden" }}
      className="fixed z-50 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-[13px] font-medium text-foreground shadow-md hover:bg-muted"
    >
      <TextQuote size={13} strokeWidth={2} />
      {t`Quote`}
    </button>,
    document.body,
  );
});
